/**
 * Session state for the SPA: who is signed in, their role, and the CSRF
 * token the api client sends on writes. Source of truth is GET /auth/me.
 *
 *   const { user, csrfToken, login, logout } = useAuth();
 *   const api = useApi();
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { createApiClient, CSRF_HEADER, readError, type ApiClient } from '@/api/client';
import { withBase } from '@/lib/basePath';
import type { Role } from './roles';

export interface AuthUser {
  email: string;
  role: Role;
}

type Status = 'loading' | 'authenticated' | 'anonymous';

export interface AuthContextValue {
  status: Status;
  user: AuthUser | null;
  csrfToken: string | null;
  login(email: string, password: string): Promise<void>;
  logout(): Promise<void>;
  api: ApiClient;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface SessionBody {
  email: string;
  role: Role;
  csrfToken: string;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<Status>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [csrfToken, setCsrfToken] = useState<string | null>(null);

  // The api client is created once; refs give it the latest values.
  const csrfRef = useRef<string | null>(null);
  csrfRef.current = csrfToken;
  const locationRef = useRef(location);
  locationRef.current = location;

  const accept = useCallback((body: SessionBody) => {
    setUser({ email: body.email, role: body.role });
    setCsrfToken(body.csrfToken);
    setStatus('authenticated');
  }, []);

  const forget = useCallback(() => {
    setUser(null);
    setCsrfToken(null);
    setStatus('anonymous');
    queryClient.clear();
  }, [queryClient]);

  const api = useMemo(
    () =>
      createApiClient({
        getCsrfToken: () => csrfRef.current,
        onUnauthorized: () => {
          forget();
          const from = locationRef.current;
          if (from.pathname !== '/login') navigate('/login', { replace: true, state: { from } });
        },
      }),
    [forget, navigate],
  );

  useEffect(() => {
    let cancelled = false;
    fetch(withBase('/auth/me'), { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(async (res) => {
        if (cancelled) return;
        if (res.ok) accept((await res.json()) as SessionBody);
        else forget();
      })
      .catch(() => {
        if (!cancelled) forget();
      });
    return () => {
      cancelled = true;
    };
  }, [accept, forget]);

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await fetch(withBase('/auth/login'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) throw await readError(res);
      accept((await res.json()) as SessionBody);
    },
    [accept],
  );

  const logout = useCallback(async () => {
    try {
      await fetch(withBase('/auth/logout'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: csrfRef.current ? { [CSRF_HEADER]: csrfRef.current } : {},
      });
    } finally {
      forget();
      navigate('/login', { replace: true });
    }
  }, [forget, navigate]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, csrfToken, login, logout, api }),
    [status, user, csrfToken, login, logout, api],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export function useApi(): ApiClient {
  return useAuth().api;
}
